<template>
  <el-dialog
    v-model="openModal"
    :title="uploadErrTitle"
    :show-close="false"
    @close="closeUploadDialog"
    class="upload-tag-dialog"
    width="680"
  >
    <div class="content" v-if="store?.state?.creatorAddTagValue?.type === 1" v-loading="loading">
      <p>
        Add and remove Creator Tags in bulk by following the steps below. Please note that all Creator Tags must already exist in the Admin Portal to add them and you must use the provided template to successfully update records.
      </p>
      <dl>
        <dt>Steps</dt>
        <dd>1. Add the Creator Tag(s) in Creator Management.</dd>
        <dd>2. Download the .csv template.</dd>
        <dd>3. Customize the template for the Tags you'd like to add or remove.</dd>
        <dd>4. Upload the .csv below.</dd>
      </dl>
      <div class="upload-box">
        <CsvUpload @uploadSuccess="uploadSuccess" ref="csvUploadRef" />
      </div>
    </div>

    <div class="content" v-if="store?.state?.creatorAddTagValue?.type === 2" v-loading="loading">
      <p>Creator Tags will not be added to or removed from creator records based on the following conditions.</p>
      <CsvTagErrorTable ref="csvTable" :data="uploadData" @duplicate_removed="duplicateRemoved"/>
    </div>

    <div class="bottom-space">
      <p class="max-creators">You can upload tags for maximum of 500 creators at once.</p>
      <div class="btn-box">
        <el-button @click="handleCancelDialog()" size="large">Cancel</el-button>
        <el-button
          v-if="store?.state?.creatorAddTagValue?.type === 1"
          size="large"
          @click="goStep2"
          type="primary"
          :disabled="uploadData.length === 0"
          >Preview</el-button
        >
        <el-button
          v-if="store?.state?.creatorAddTagValue?.type === 2"
          type="primary"
          size="large"
          @click="checkTable"
          >Continue</el-button
        >
      </div>
    </div>
  </el-dialog>

  <el-drawer v-model="drawerOpen" direction="rtl" size="100vw" :with-header="false" class="upload-tag-drawer">
    <div class="header">
      <div class="title">
        <h2>Preview Upload</h2>
        <div class="desc">
          Your upload is not complete. Please review the changes below to confirm and click confirm to finalize.
        </div>
      </div>
      <i @click="beforeCloseDrawer"><svg-icon name="close" /></i>
    </div>

    <div class="content" v-loading="loading">
      <div class="content-head">
        <p>Creators Tags added to {{ addNum }} creator records.</p>
        <p>Creator Tags removed from {{ removeNum }} creator records.</p>
      </div>

      <div class="updated-creators-wrapper">
        <div class="header-bar">
          <span class="title">Updated creators</span>

          <div class="actions">
            <Pagenavi
              :disabled="tableData.length === 0"
              :page_current="pageRow.current_page"
              :page_total="pageRow.total_page"
              @goto-page="gotoPage"
            />
            <el-button text @click="onRemoveHandle" :disabled="selectedRows.length === 0">Remove</el-button>
            <el-button type="primary" @click="onConfirmHandle">Confirm</el-button>
          </div>
        </div>

        <el-table
          class="csv-tag-table"
          :data="tableData"
          @selection-change="handleSelectionChange"
          width="100%"
          max-height="calc(95vh - 240px)"
        >
          <!-- checkbox -->
          <el-table-column type="selection" width="50" />

          <el-table-column label="Creator Name" prop="name" />

          <el-table-column label="Email" prop="email" />

          <el-table-column label="TikTok" prop="tiktok" />

          <el-table-column label="Instagram" prop="instagram" />

          <el-table-column label="Tags (added)">
            <template #default="scope">
              <div class="tag-row">
                <Badage
                  :class="['tag-item-box']"
                  v-for="(tag, k) in scope.row.added_merchant_account_tags || []"
                  :key="k"
                  :value="tag.tag"
                  :show_tippy="true"
                />
              </div>
            </template>
          </el-table-column>

          <el-table-column label="Tags (removed)">
            <template #default="scope">
              <div class="tag-row">
                <Badage
                  :class="['tag-item-box']"
                  v-for="(tag, k) in scope.row.removed_merchant_account_tags || []"
                  :key="k"
                  :value="tag.tag"
                  :show_tippy="true"
                />
              </div>
            </template>
          </el-table-column>

          <!-- Options -->
          <el-table-column label="Options" width="100">
            <template #default="scope">
              <div class="svg-row">
                <!-- <svg-icon name="pen" @click="onEdit(scope.row)" /> -->
                <svg-icon name="remove" @click="onDelete(scope.row)" />
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>
  </el-drawer>

  <el-dialog v-model="is_show_edit_creator_tag" :show-close="false" class="edit-creator-tag-dialog" width="850">
    <div class="title">
      <h2>Edit Creator</h2>
      <svg-icon name="close" @click="beforeCloseCreatorTag" />
    </div>
    <div class="creator-card">
      <div class="creator-info">
        <h3>Creator name</h3>
        <div>Email@email.com</div>
        <div>Instagram</div>
        <div>TikTok</div>
      </div>
      <div class="tag-section">
        <div class="section-title">Creator tags</div>

        <div class="row">
          <div class="label">Added</div>

          <el-select
            v-model="addedSelect"
            multiple
            placeholder="Select and tag"
            class="tag-select"
            popper-class="tag-dropdown"
            ref="addedSelectRef"
          >
            <el-option v-for="tag in allTags" :key="tag" :label="tag" :value="tag" />
          </el-select>

          <el-button @click="addToAdded"><svg-icon name="plus" /></el-button>
        </div>
        <div class="row">
          <div class="label">Removed</div>

          <el-select
            v-model="removedSelect"
            multiple
            placeholder="Select and tag"
            class="tag-select"
            popper-class="tag-dropdown"
            ref="removedSelectRef"
          >
            <el-option v-for="tag in allTags" :key="tag" :label="tag" :value="tag" />
          </el-select>

          <el-button @click="addToRemoved"><svg-icon name="plus" /></el-button>
        </div>
      </div>
    </div>
    <div class="btn-row">
      <button class="discard-btn" @click="beforeCloseCreatorTag">Discard</button>
      <el-button type="primary" @click="save">Save changes</el-button>
    </div>
  </el-dialog>
  
  <el-dialog class="err-table-alert"  v-model="errTableAlert"  :title="errTableAlertTitle" :show-close="false" width="300">
    <p>We found some other issues that require you to take action</p>
    <div class="btn-block">
      <el-button type="primary" @click="errTableAlert=false">Back</el-button>
    </div>
  </el-dialog>

  <!-- edit one creator tag dialog -->
  <el-dialog v-model="discard_open" title="Would you like to discard these edits?" :show-close="false" width="400">
    <p>This creator’s tags will not be updated.</p>
    <template #footer>
      <div class="dialog-footer">
        <el-button @click="discard_open = false">Cancel</el-button>
        <el-button
          type="primary"
          @click="
            is_show_edit_creator_tag = false;
            discard_open = false;
          "
        >
          Confirm
        </el-button>
      </div>
    </template>
  </el-dialog>

  <el-dialog
    v-model="tableAlertDialog"
    :show-close="false"
    @close="closeUploadDialog"
    class="table-alert-dialog"
    width="600"
    :title="tableDialogContent.title"
  >
    <div class="content-p" v-html="tableDialogContent.content"></div>
    <template #footer>
      <div class="dialog-footer">
        <el-button @click="tableAlertDialog = false">Back</el-button>
        <el-button type="primary" @click="closeTableAlertDialog" v-if="tableAlertType !== 'noSave'">
          Confirm
        </el-button>
        <el-button
          type="primary"
          @click="
            tableAlertDialog = false;
            store.state.creatorAddTagValue.drawer_open = false;
            removeRouteQuery();
          "
          v-if="tableAlertType === 'noSave'"
        >
          Discard</el-button
        >
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, reactive, computed, getCurrentInstance, watch, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { ElDialog, ElButton, ElDrawer, ElTable, ElTableColumn, ElSelect, ElOption } from 'element-plus';
import CsvUpload from '@/components/creator/CsvTagUpload.vue';
import CsvTagErrorTable from '@/components/creator/CsvTagErrorTable.vue';
import Badage from '@/components/ui/Badage.vue';
import Pagenavi from '@/components/ui/Pagenavi.vue';
import { useNotification } from '@/components/ui/notification';
import { useRoute, useRouter } from 'vue-router';

// vuex
const store = useStore();
const app = getCurrentInstance();
const ajax = app.appContext.config.globalProperties.$ajax;
const eventBus = app.appContext.config.globalProperties.EventBus;
const { openNotBottom } = useNotification();
const route = useRoute();
const router = useRouter();

const loading = ref(false);
const csvUploadRef = ref(null);
const uploadData = ref([]);
const csvTable = ref(null);
const tableData = ref([]);
const selectedRows = ref([]);
const pageRow = ref({
  current_page: 1,
  total_page: 1,
  per_page: 10,
  totolNum: 0,
});
const addNum = ref(0);
const removeNum = ref(0);

const tableAlertDialog = ref(false);
const tableAlertType = ref(''); // 'remove' or 'confirm'
const removeItem = ref([]);
const errTableAlert=ref(false);
const errTableAlertTitle=ref(`Please resolve "Other errors" to continue`);
const isRouteOpenDrawer= ref(false);
//-------------------------------
// edit one creator tag dialog data
const is_show_edit_creator_tag = ref(false);
const allTags = ['Cat', 'Dog', 'Rabbit', 'Snake'];
const addedSelect = ref([]);
const removedSelect = ref([]);
const addedSelectRef = ref(null);
const removedSelectRef = ref(null);
const discard_open = ref(false);
//-----------------------------------

const openModal = computed(() => {
  return store?.state?.creatorAddTagValue?.open;
});
const drawerOpen = computed(() => {
  return store?.state?.creatorAddTagValue?.drawer_open;
});

const uploadErrTitle = computed(() => {
  let title = '';
  if (store?.state?.creatorAddTagValue?.type === 1) {
    title = 'Add or Remove Creator Tags by .CSV';
  } else if (store?.state?.creatorAddTagValue?.type === 2) {
    title = 'We found the following errors in your upload:';
  } else if (store?.state?.creatorAddTagValue?.type === 3) {
    title = 'Import failed';
  }
  return title;
});

const tableDialogContent = computed(() => {
  let removeCount = {
    title: `Remove ${removeItem.value.length} creator from bulk upload?`,
    content: `${removeItem.value.length === 1 ? 'This' : 'These'} creator’s tags will not be updated.`,
  };

  let comfirmContent = {
    title: `Confirm changes`,
    content: `This may change commission rate, communication templates, and/or storefront experience, depending on your settings. Please confirm below or select back to edit.<ul><li>You are adding Creator Tags to ${addNum.value} creator records</li><li>You are removing Creator Tags from ${removeNum.value} creator records</li></ul>`,
  };

  let noSave = {
    title: `Your upload is not complete`,
    content: `Creator Tags have not been updated. Please go back to complete your upload or discard.`,
  };
  return tableAlertType.value === 'remove' ? removeCount : tableAlertType.value === 'noSave' ? noSave : comfirmContent;
});

const canNotCheckTable = computed(() => {
  const hasDuplicateMemberId = () => {
    const arr = uploadData.value.duplicate_member_id_rows;
    const ids = arr?arr.map((item) => item.member_id): null;
    return new Set(ids).size !== ids?.length;
  };

  return hasDuplicateMemberId();
});


watch(() => drawerOpen.value,
  (val) => {
    if(val && !uploadData.value.batch_id){
      isRouteOpenDrawer.value = true;
      uploadData.value.batch_id=route.query.previewTag;
      checkTable();
    }
  }
);

watch(() => openModal.value,
  (val) => {
    if (val && !isRouteOpenDrawer.value) {
      console.log('open upload dialog');
      uploadData.value = [];
      loading.value = false;
    }
  }
);

watch(() => uploadData.value, (val) => {
  console.log('uploadData changed', val);
});
//+++++++++++++++++++++++++++++++++++++
const handleCancelDialog=()=>{
  if(store.state.creatorAddTagValue.type === 1){
   closeUploadDialog();
  }else{
    store.state.creatorAddTagValue.type = 1
  }
};

const closeUploadDialog = () => {
  store.state.creatorAddTagValue.open = false;
  pageRow.valuecurrent_page = 1;

  if (csvUploadRef.value) {
    csvUploadRef.value.clearCsv();
  }
};

const uploadSuccess = (data) => {
  uploadData.value = data;
};

const goStep2 = () => {
  if (
    uploadData.value.duplicate_member_id_rows.length > 0 ||
    uploadData.value.not_matched_tag_text_array.length > 0 ||
    uploadData.value.not_valid_tag_text_array.length > 0 ||
    uploadData.value.unmatch_member_id_rows.length > 0
  ) {
    store.state.creatorAddTagValue.type = 2;
  } else {
    checkTable();
  }
};

const Reupload = () => {
  store.state.creatorAddTagValue.type = 1;
  uploadData.value = [];
};

const checkTable = () => {

  if(canNotCheckTable.value && !isRouteOpenDrawer.value){
    errTableAlert.value=true;
    return;
  }

  loading.value = true;
  ajax
    .get(`/batch-tag-update/get-rows?batch_id=${uploadData.value.batch_id}&page=${pageRow.value.current_page}`)
    .then(async(res) => {
      console.log('res', res.data.data);
      tableData.value = [];
      res.data.data.data.forEach((e) => {
        if (e.anchor_creator) {
          e.name = e.anchor_creator.first_name + ' ' + e.anchor_creator.last_name;
          e.email = e.anchor_creator.email ?? '--';
          e.tiktok = e.anchor_creator.tiktok_handle ?? '--';
          e.instagram = e.anchor_creator.instagram_handle ?? '--';

          tableData.value.push(e);
        }
      });
      pageRow.value.current_page = res.data.data.current_page;
      pageRow.value.total_page = res.data.data.last_page;
      pageRow.value.totolNum = res.data.data.total;
      await getConfirmNum();

      if (!store.state.creatorAddTagValue.drawer_open) {
        store.state.creatorAddTagValue.drawer_open = true;
        store.state.creatorAddTagValue.open = false;
        router.replace({
          path: route.path,
          query: {
            ...route.query,
            previewTag: uploadData.value.batch_id,
          }
        });
      }
      loading.value = false;
    })
    .catch((err) => {
      console.log('err', err);
      csvTable.value.openAlertDialog();
      loading.value = false;
    });
};

const duplicateRemoved = (rows) => {
  uploadData.value.duplicate_member_id_rows=rows;
  console.log('uploadData after remove duplicate', uploadData.value);
};
//------------------------------
//table drawer methods
const handleSelectionChange = (rows) => {
  selectedRows.value = rows;
};

const onEdit = (row) => {
  is_show_edit_creator_tag.value = true;
};

const onDelete = (row) => {
  removeItem.value = [row];
  tableAlertType.value = 'remove';
  tableAlertDialog.value = true;
};

const onRemoveHandle = () => {
  removeItem.value = selectedRows.value;
  tableAlertType.value = 'remove';
  tableAlertDialog.value = true;
};

const onRemove = () => {
  loading.value = true;
  let form_data = new FormData();
  removeItem.value.forEach((row) => {
    form_data.append('batch_creator_tag_update_row_ids[]', row.id);
  });
  ajax
    .post('/batch-tag-update/remove-rows', form_data)
    .then(() => {
      removeItem.value.forEach((row) => {
        const index = tableData.value.indexOf(row);
        if (index > -1) {
          tableData.value.splice(index, 1);
        }
      });
      openNotBottom('Removed successfully', 'success');
      pageRow.value.current_page = 1;
      checkTable();
    })
    .catch((error) => {
      console.error('Error removing rows:', error);
      openNotBottom('Failed to remove the rows', 'error');
      loading.value = false;
    });
};

const onConfirmHandle = () => {
  tableAlertType.value = 'confirm';
  tableAlertDialog.value = true;
};

const gotoPage = (page) => {
  pageRow.value.current_page = page;
  checkTable();
};

const onConfirm = () => {
  loading.value = true;
  let form_data = new FormData();
  form_data.append('batch_id', uploadData.value.batch_id);
  ajax
    .post('/batch-tag-update/apply-rows', form_data, {
      method: 'post',
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(() => {
      openNotBottom(`${pageRow.value.totolNum} creator records updated.`, 'success');
      tableAlertDialog.value = false;
      store.state.creatorAddTagValue.drawer_open = false;
      eventBus.emit('update-table-creator');
      loading.value = false;
      removeRouteQuery();
      // Trigger queue update event
      setTimeout(()=>{
        eventBus.emit('ready-queue-update');
      },2000)
    })
    .catch((err) => {
      console.log('err', err);
      openNotBottom('Failed to confirm the changes', 'error');
      loading.value = false;
    });
};

const removeRouteQuery = () => {
  router.replace({
    query: {
      ...route.query,
      previewTag: undefined
    }
  });
  const url = new URL(window.location)
  const urlParams = url.searchParams;
  urlParams.delete('previewTag');
  isRouteOpenDrawer.value = false;
};

const getConfirmNum = async () => {
  try {
    const res = await ajax.get(
      `/batch-tag-update/confirm-update-rows?batch_id=${uploadData.value.batch_id}`
    );

    addNum.value = res.data.data.added_count;
    removeNum.value = res.data.data.removed_count;

  } catch (err) {
    console.log('err', err);
  }
};


//-----------------------
// edit one creator tag dialog methods
const addToAdded = () => {
  addedSelectRef.value.focus();
  addedSelectRef.value.toggleMenu();
};

const addToRemoved = () => {
  removedSelectRef.value.focus();
  removedSelectRef.value.toggleMenu();
};

const beforeCloseCreatorTag = (done) => {
  discard_open.value = true;
};
const save = () => {
  is_show_edit_creator_tag.value = false;
};

//-------------------------------
const closeTableAlertDialog = () => {
  if (tableAlertType.value === 'remove') {
    onRemove();
  } else if (tableAlertType.value === 'confirm') {
    onConfirm();
  }
  tableAlertDialog.value = false;
};

const beforeCloseDrawer = () => {
  tableAlertType.value = 'noSave';
  tableAlertDialog.value = true;
};
</script>

<style lang="scss">
.el-overlay {
  height: 100vh;
}

.upload-tag-dialog {
  .el-dialog__header {
    padding-bottom: 0;
  }

  .el-dialog__title {
    font: var(--font-med-24);
  }

  h2 {
    font: var(--font-med-24);
    color: var(--black-100);
  }

  .content {
    margin: 16px 0;
    color: var(--black-90);
    font-size: 16px;

    a {
      font: var(--font-med-16);
    }

    p {
      margin-bottom: 16px;
    }

    dl {
      margin: 16px 0;

      dt {
        font: var(--font-med-16);
      }

      dd {
        margin-left: 8px;
        list-style: decimal;
      }
    }
  }

  .empty-box {
    p {
      margin-bottom: 0;
      margin-left: 4px;
    }

    .el-button {
      font-size: 16px;
      padding: 12px 16px;
      height: 40px;
    }
  }

  .btn-box {
    display: flex;
    justify-content: flex-end;
  }

  .bottom-space{
    display: flex;
    justify-content: space-between;
    align-items: center
  }
}

.upload-tag-drawer {
  padding: 16px 24px;

  .header {
    .title {
      flex: 1;
      h2 {
        font: var(--font-demi-24);
        color: var(--black-90);
      }

      .desc {
        font-size: 14px;
        color: var(--black-90);
      }
    }
    display: flex;
    align-items: center;
    justify-content: space-between;

    i {
      cursor: pointer;
      width: 40px;
      height: 40px;

      svg {
        width: 16px;
        height: 16px;
      }
    }
  }

  .content-head {
    border-top: solid 1px var(--border);
    border-bottom: solid 1px var(--border);
    margin: 16px 0;
    padding: 8px 0;
    font-size: 14px;
  }

  .updated-creators-wrapper {
    width: 100%;
    margin-top: 16px;
  }

  .header-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 8px 0;
  }

  .title {
    font: var(--font-med-20);
    flex: 1;
  }

  .actions {
    display: flex;
    gap: 8px;
  }
}

.csv-tag-table {
  margin: 8px 0;
  thead {
    position: sticky;
    z-index: 3;
    color: var(--black-90);

    tr {
      background: var(--bg-10);
    }
  }

  th.el-table__cell {
    background-color: unset !important;
  }

  td.el-table__cell {
    border-top: solid 1px var(--black-20);
  }

  th:last-child,
  td:last-child {
    text-align: left !important;
  }

  .tag-row {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .svg-row {
    display: flex;
    gap: 16px;
    svg {
      cursor: pointer;
      width: 16px;
      height: 16px;
    }
  }

  th .el-checkbox {
    .el-checkbox__inner {
      background-color: unset;
      &::before {
        display: none;
      }
    }
    .is-checked {
      .el-checkbox__inner {
        background-color: var(--el-checkbox-checked-bg-color);
        &::before {
          display: block;
        }
      }
    }
  }
}

//------------------------------------
.edit-creator-tag-dialog {
  padding: 0;
  .el-dialog__header {
    display: none;
  }

  .title {
    padding: 8px 24px;
    display: flex;
    align-items: center;
    border-bottom: solid 1px var(--border);
    h2 {
      font: var(--font-demi-16);
      flex: 1;
    }
    svg {
      cursor: pointer;
      width: 16px;
      height: 16px;
    }
  }

  .creator-card {
    padding: 16px 24px;
  }
  /* Creator Info */
  .creator-info h3 {
    font: var(--font-med-16);
    margin-bottom: 8px;
  }

  .section-title {
    font: var(--font-med-14);
    margin: 8px 0 16px;
  }

  /* Row Layout */
  .row {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    justify-content: space-between;
    gap: 16px;
    position: relative;

    .label {
      width: 80px;
      font: var(--font-med-12);
    }

    .el-button {
      width: 24px;
      height: 24px;
      position: absolute;
      right: 8px;
      svg {
        width: 16px;
        height: 16px;
      }
    }

    .el-select__wrapper {
      height: 40px;
    }
  }

  /* Select */
  .tag-select {
    flex: 1;
  }

  /* Dropdown Style（右下角白色 UI）*/
  .tag-dropdown {
    border-radius: 12px !important;
    padding: 12px !important;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  }

  /* Bottom Buttons */
  .btn-row {
    display: flex;
    justify-content: flex-end;
    padding: 16px 24px;
    border-top: solid 1px var(--border);
  }

  .discard-btn {
    border: none;
    background: none;
    margin-right: 16px;
    color: #666;
    cursor: pointer;
  }
}

.table-alert-dialog {
  ul {
    margin: 16px;
  }
  ul li {
    font: var(--font-med-16);
    margin-bottom: 8px;
    list-style-type: disc;
  }

  .content-p {
    font-size: 16px;
  }
}

.err-table-alert{
  .btn-block{
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
  }
}

//tag option style change
/* 下拉外層 */
.tag-dropdown {
  border-radius: 10px;
  padding: 6px 0;
}

/* 每個 option */
.tag-dropdown .el-select-dropdown__item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 6px;
  margin: 2px 6px;
  font-size: 14px;
  transition: 0.15s;
}

/* 未選取時 - 自己畫方框 */
.tag-dropdown .el-select-dropdown__item::before {
  content: '';
  width: 16px;
  height: 16px;
  border: 2px solid #bfc2cc; /* 淺灰 */
  border-radius: 4px;
  margin-right: 10px;
  box-sizing: border-box;
}

/* 滑過 */
.tag-dropdown .el-select-dropdown__item:hover {
  background: #f2f4f7;
}

/* 已選取時的打勾框 */
// .tag-dropdown .el-select-dropdown__item::before{
//   background: var(--dark-blue);
//   position: relative;
// }

.tag-dropdown .is-selected::before {
  background: var(--dark-blue);
  position: relative;
  border: none;
}

/* 勾勾 */
.tag-dropdown .is-selected::after {
  content: '';
  position: absolute;
  left: 14px;
  width: 5px;
  height: 9px;
  right: unset !important;
  border-right: 2px solid var(--dark-blue);
  border-bottom: 2px solid var(--dark-blue);
  transform: rotate(45deg);
  background-color: var(--white) !important;
  z-index: 1;
}
</style>
